import { HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActionsSubject, Store } from '@ngrx/store';
import { GuardhouseErrorCodes } from '@rd/errors/guardhouse-error-codes.type';
import {
  isGuardhouseInternalError,
  isGuardhouseScopeError,
  isGuardhouseTokenError,
} from '@rd/errors/guardhouse-error-codes.utils';
import { HttpError } from '@rd/types';
import { EMPTY, Observable, throwError, timer } from 'rxjs';
import { filter, mergeMap, switchMap } from 'rxjs/operators';
import * as actions from '../../auth/store/actions/auth.actions';
import * as mfaActions from '../../mfa/store/actions/mfa.actions';
import { CoreState } from '../../store/reducers';

const GH_TIMEOUT_RETRY_INTERVAL = 1000;
const GH_MAX_REQUEST_RETRIES = 3;

@Injectable({
  providedIn: 'root',
})
export class NydusNetworkErrorHandlerService {
  constructor(
    private store: Store<CoreState>,
    private actionsSubject: ActionsSubject,
  ) {}

  handleErrorStream(
    errors$: Observable<HttpErrorResponse | Error>,
    requestUrl: string,
  ): Observable<number> {
    return errors$.pipe(
      // If error is thrown here using throwError, the request won't be retried and the loop will exit.
      // In any other cases (e.g. returning timer), the original request will be retried using retryWhen in NydusNetworkApiDataService.
      mergeMap(
        (errorResponse: HttpErrorResponse | Error, retryAttempt: number) => {
          const httpError = this.transformError(requestUrl, errorResponse);

          if (retryAttempt >= GH_MAX_REQUEST_RETRIES) {
            this.store.dispatch(new actions.MaximumRetriesFail(httpError));
            return throwError(errorResponse);
          } else {
            return this.handleError(httpError);
          }
        },
      ),
    );
  }

  private handleError(httpError: HttpError): Observable<number | never> {
    if (httpError.status === 401) {
      return this.handleAuthError(httpError);
    }

    return throwError(httpError);
  }

  private handleAuthError(httpError: HttpError): Observable<number | never> {
    const guardhouseAuthErrors = httpError.headers['x-auth-errors'];
    const mfaError = httpError.errors?.find(
      (e) => e.code === GuardhouseErrorCodes.MFA_REQUIRED,
    );
    if (mfaError) {
      // Initiate MFA
      this.store.dispatch(
        mfaActions.RedirectMFA({ level: mfaError.metadata?.level ?? 2 }),
      );
      // Return Never so that downstream consumers of this
      // Service don't have an error
      return EMPTY;
    }

    if (guardhouseAuthErrors && guardhouseAuthErrors.length > 0) {
      const errorCodes = (guardhouseAuthErrors[0] as string).split(',');
      return this.handleGuardhouseAuthError(httpError, errorCodes);
    } else {
      return this.handleSentinelAuthError(httpError);
    }
  }

  private handleSentinelAuthError(
    error: HttpError,
  ): Observable<number | never> {
    const errorCodes = error.errors?.map((err) => err.code);

    if (errorCodes && errorCodes.some((code) => isGuardhouseScopeError(code))) {
      this.store.dispatch(new actions.InvalidScopeFail(error));
      return throwError(error);
    } else {
      // TODO: TS-strict-mode fix later
      // @ts-ignore
      return this.handleCommonAuthError(error, errorCodes);
    }
  }

  private handleGuardhouseAuthError(
    error: HttpError,
    errorCodes: string[],
  ): Observable<number | never> {
    if (errorCodes.some((code) => code === GuardhouseErrorCodes.TIMEOUT)) {
      return timer(GH_TIMEOUT_RETRY_INTERVAL);
    } else {
      return this.handleCommonAuthError(error, errorCodes);
    }
  }

  private handleCommonAuthError(
    error: HttpError,
    errorCodes: string[],
  ): Observable<number | never> {
    if (
      errorCodes.some(
        (code) => code === GuardhouseErrorCodes.INVALID_TOKEN_EXPIRY,
      )
    ) {
      this.store.dispatch(new actions.TokenExpiredFail(error));
      return this.waitForTokenRefresh();
    } else if (
      errorCodes.some(
        (code) =>
          isGuardhouseInternalError(code) || isGuardhouseTokenError(code),
      )
    ) {
      this.store.dispatch(new actions.InternalFail(error));
    } else if (
      errorCodes.some((code) => code === GuardhouseErrorCodes.NO_PERMISSION)
    ) {
      this.store.dispatch(new actions.NoPermissionFail(error));
    }
    return throwError(error);
  }

  private waitForTokenRefresh(): Observable<number> {
    return this.actionsSubject.pipe(
      filter(
        (action) =>
          action.type === actions.AuthActionTypes.LOAD_IFRAME_TOKEN_SUCCESS ||
          action.type === actions.AuthActionTypes.LOAD_RTR_TOKEN_SUCCESS,
      ),
      switchMap(() => timer(GH_TIMEOUT_RETRY_INTERVAL)),
    );
  }

  private isErrorResponse(
    errorResponse: HttpErrorResponse | Error,
  ): errorResponse is HttpErrorResponse {
    return (errorResponse as HttpErrorResponse).statusText !== undefined;
  }

  private transformError(
    requestUrl: string,
    errorResponse: HttpErrorResponse | Error,
  ): HttpError {
    if (this.isErrorResponse(errorResponse)) {
      const { message, status, statusText, headers, url, error } =
        errorResponse;

      return {
        message,
        status,
        statusText,
        headers: this.extractHeaders(headers),
        url: url || '', // use full URL given by errorResponse when available
        errors: error ? error.errors : [],
      };
    } else {
      return {
        message: errorResponse.message,
        // TODO: TS-strict-mode fix it later
        // @ts-ignore
        status: null,
        // @ts-ignore
        statusText: null,
        // @ts-ignore
        headers: null,
        url: requestUrl,
        errors: [],
      };
    }
  }

  private extractHeaders(headers: HttpHeaders): { [key: string]: string[] } {
    return headers.keys().reduce((res, key) => {
      // @ts-ignore
      res[key] = headers.getAll(key);
      return res;
    }, {});
  }
}
